import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Gift } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { namespace } from '@common-config/constants';
import { backendApi, trackReferralLink } from '@common-reducers/backend-api/backend-api';
import { useAnalyticsService } from '@common-reducers/hooks/use-analytics-service';
import { isExtensionContext } from '@common-services/detect-context';
import { BRAND } from '@common/styles/brand-tokens';
import { earnedLabel, offerCopy, whatsappShareUrl } from './referral-share';

export interface ReferralShareCardProps {
  /** `menu`: compact, inside the account menu. `page`: full width, on the billing page.
   *  `inline`: narrower card without the stats row, for a moment of delight (post-payment). */
  variant: 'menu' | 'page' | 'inline';
  /** Where the visitor came from (nav, nudge, post_pay …), stamped on the share events. */
  via?: string;
}

const STYLES = `
.rsc-btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;border-radius:8px;font-weight:600;cursor:pointer;text-decoration:none;white-space:nowrap;transition:filter .15s ease,border-color .15s ease}
.rsc-label{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.rsc-btn:focus-visible{outline:2px solid ${BRAND.accent};outline-offset:2px}
.rsc-copy{background:${BRAND.brand};color:${BRAND.brandText};border:none}
.rsc-copy:hover{filter:brightness(1.06)}
.rsc-wa{background:var(--color-surface,#fff);color:${BRAND.text};border:1px solid ${BRAND.trackStrong}}
.rsc-wa:hover{border-color:${BRAND.text}}
`;

/**
 * Refer-a-friend (#186) share card: the owner's link, Copy + WhatsApp share, and counts.
 * Web app only for now; renders nothing while loading, on error (e.g. a non-owner),
 * or while the program is off.
 */
export default function ReferralShareCard({ variant, via }: ReferralShareCardProps) {
  const { t } = useTranslation(namespace);
  const analyticsService = useAnalyticsService();
  const skip = isExtensionContext();
  const { data } = backendApi.useGetMyReferralQuery(undefined, { skip });
  const [copied, setCopied] = useState(false);

  const offer = offerCopy(data?.offer);
  if (skip || !data?.enabled || !data.shareUrl || !offer) return null;

  const shareUrl = data.shareUrl;
  const compact = variant === 'menu';
  const stats = data.stats ?? { signedUp: 0, pending: 0, rewarded: 0, earnedCents: {} };
  const earned = earnedLabel(stats.earnedCents);
  const friendOffer = offer.cap
    ? t('referral.friendOfferCapped', { percent: offer.percent, cap: offer.cap })
    : t('referral.friendOffer', { percent: offer.percent });
  const shareMessage = t('referral.shareMessage', { percent: offer.percent, url: shareUrl });

  const track = (action: string) => analyticsService.event('referral_share', { category: 'referral', action, surface: variant, ...(via ? { via } : {}) });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      track('copy_link');
      trackReferralLink({ kind: 'copied', surface: variant });
    } catch {
      window.prompt(t('referral.copyLink'), shareUrl);
    }
  };

  // Compact (menu): one row, equal-width buttons; labels ellipsize (.rsc-label) instead of wrapping or spilling.
  const buttonSize: React.CSSProperties = compact
    ? { height: 30, padding: '0 8px', fontSize: 12, flex: '1 1 0', minWidth: 0, overflow: 'hidden' }
    : { height: 38, padding: '0 16px', fontSize: 14 };

  return (
    <div
      data-testid={`referral-share-card-${variant}`}
      style={{
        border: `1px solid ${BRAND.border}`,
        borderRadius: compact ? 10 : 14,
        background: BRAND.wash(BRAND.accent, 14),
        color: BRAND.text,
        padding: compact ? 10 : 20,
        // Menu: the same horizontal inset as the .smallMenuItem rows (margin-inline: 8px) and the full row width; RTL mirrors by itself.
        ...(compact ? { marginBlock: 8, marginInline: 8 } : { margin: variant === 'inline' ? '20px auto 0' : '24px auto 0' }),
        maxWidth: compact ? undefined : variant === 'inline' ? 480 : 720,
        width: variant === 'inline' ? '100%' : undefined,
        boxSizing: 'border-box',
      }}
    >
      <style>{STYLES}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: compact ? 13 : 17 }}>
        <Gift size={compact ? 16 : 20} color="#2B7FFF" aria-hidden />
        {offer.credit ? t('referral.title', { percent: offer.percent, credit: offer.credit }) : t('referral.titleNoCredit', { percent: offer.percent })}
      </div>

      <div style={{ color: BRAND.muted, fontSize: compact ? 12 : 14, lineHeight: 1.45, marginTop: 6 }}>
        {friendOffer}
        {offer.credit && <> {t('referral.yourReward', { credit: offer.credit, days: offer.holdDays })}</>}
      </div>

      {!compact && (
        <div
          style={{ marginTop: 14, padding: '8px 12px', borderRadius: 8, border: `1px solid ${BRAND.border}`, background: BRAND.surface, fontFamily: 'monospace', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {shareUrl}
        </div>
      )}

      <div style={{ display: 'flex', gap: compact ? 6 : 8, marginTop: compact ? 8 : 12, flexWrap: compact ? 'nowrap' : 'wrap' }}>
        <button type="button" className="rsc-btn rsc-copy" style={buttonSize} onClick={copy} data-testid="referral-copy-link">
          {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
          <span className="rsc-label">{copied ? t('referral.copied') : t('referral.copyLink')}</span>
        </button>
        <a
          className="rsc-btn rsc-wa"
          style={buttonSize}
          href={whatsappShareUrl(shareMessage)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            track('whatsapp');
            trackReferralLink({ kind: 'shared', surface: variant, channel: 'whatsapp' });
          }}
          aria-label={t('referral.shareOnWhatsApp')}
          data-testid="referral-share-whatsapp"
        >
          <FaWhatsapp size={14} color="#25D366" aria-hidden />
          <span className="rsc-label">{compact ? t('referral.shareShort') : t('referral.shareOnWhatsApp')}</span>
        </a>
      </div>

      {variant !== 'inline' && (
        <div style={{ color: BRAND.muted, fontSize: compact ? 11 : 13, marginTop: compact ? 8 : 12 }} data-testid="referral-stats">
          {t('referral.stats', { signedUp: stats.signedUp, pending: stats.pending, rewarded: stats.rewarded })}
          {earned && <> · {t('referral.earned', { amount: earned })}</>}
        </div>
      )}
    </div>
  );
}
